import { Directive, Input, OnInit, forwardRef, NgIterable, ViewContainerRef, TemplateRef, Output, EventEmitter } from '@angular/core';
import { AbstractListView } from './abstract-list-view';
import { SortEvent, SortFieldComponent } from '../components/sort-field/sort-field.component'
import { FilterFieldDirective } from './filter-field.directive';
import { BehaviorSubject } from 'rxjs';

export const getValueFromPath = function(o, path) {
  for (var i = 0, n = path.length; i < n && o; ++i) {
      var k = path[i];
      if (k in o) {
          o = o[k];
      } else {
          return;
      }
  }
  return o;
}

export type SortCriteria = SortEvent

@Directive({
  selector: '[fesListView][fesListViewOf]',
  exportAs: 'fesListView',
  providers:  [{ provide: AbstractListView, useExisting: forwardRef(() => ListViewDirectiveOf) }]
})
export class ListViewDirectiveOf<T,U extends NgIterable<T> = NgIterable<T>>
      extends AbstractListView<T,U> implements OnInit {

  public srcList: U;  // keep track of original list (note: must be reassign for filter to detect changes and will not work if you update existing collection)
  public _viewList: Array<T> = []; // work list where sorts and filters are applied
  public _srcIndices: Array<number> = [];

  public sortFields: SortFieldComponent<T,U>[] = [];

  public filterFields: FilterFieldDirective<T,U>[] = [];

  @Input('fesListViewOf') private set setSrcList(srcList: U) {
    this.srcList = srcList;
    this.filter()
  }

  @Output('filtered') filtered$ = new BehaviorSubject<Array<T>>([])
  @Output('sorted') sorted$ = new EventEmitter<{criteria: SortCriteria, list: Array<T>}>()

  constructor(private _viewContainer: ViewContainerRef, private _template: TemplateRef<any>) {
    super()
  }

  ngOnInit(): void {
    let context = { $implicit: this._viewList, sourceIndices: this._srcIndices, filtered$: this.filtered$ }
    this._viewContainer.createEmbeddedView(this._template, context )
  }

  public filter = () => {
    let srcArray = this.srcList ? Array.from(this.srcList) : []
    let indices = srcArray.map((row,index)=>index)
    this.filterFields.forEach(filterField => {
      if ( filterField.isApplicable() ) {
        filterField.prepare()
        const namePath = filterField.name.split('.')
        indices = indices.filter( (rowIndex) => {
          const row = srcArray[rowIndex]
          const resolvedVal = filterField.valueResolver ? filterField.valueResolver(row,namePath) : getValueFromPath(row,namePath)
          return filterField.accept(resolvedVal)
        })
      }
    });
    this._srcIndices.length = 0
    this._srcIndices.push(...indices)
    this.sort(srcArray)
    this.filtered$.next(this._viewList)
  }

  public onSort({ name, direction }: SortEvent) {
    this.sortFields.forEach(field => {
      if (field.name !== name) {
        field.direction = '';
      } else if (direction) {
        field.direction = direction;
      }
    })
    this.sort(Array.from(this.srcList))
}


sort( srcArray : Array<T>) {
  if ( !srcArray ) {
    srcArray = []
  }
  let activeSortField = this.sortFields.find(field => field.direction)
  if ( activeSortField ) {
    let valueResolver = activeSortField.valueResolver || getValueFromPath;
    let name = activeSortField.name;
    this._srcIndices.sort((aIndex, bIndex) => {
      const a = srcArray[aIndex]
      const b = srcArray[bIndex]
      const fieldPath = name.split('.')
      const aVal = valueResolver(a,fieldPath)
      const bVal = valueResolver(b,fieldPath)
      const res = activeSortField.sortFn(aVal,bVal);
      return activeSortField.direction === 'asc' ? res : -res;
    })
  }
  this._viewList.length = 0
  this._viewList.push(...this._srcIndices.map(srcIndex=>srcArray[srcIndex]));
  if ( activeSortField ) {
    this.sorted$.emit({
      criteria: {
        name: activeSortField.name,
        direction: activeSortField.direction
      },
      list: this._viewList
    })
  }
}

public addSortField( field : SortFieldComponent<T,U> ) {
  this.sortFields.push(field)
}

public addFilterField( field : FilterFieldDirective<T,U> ) {
  this.filterFields.push(field)
}

removeFilterField( f : FilterFieldDirective<T,U> ) {
  let index = this.filterFields.findIndex(ff=>ff==f)
  if ( index >= 0 ) {
    this.filterFields.splice(index,1)
  }
}

removeSortField( f : SortFieldComponent<T,U> ) {
  let index = this.sortFields.findIndex(sf=>sf==f)
  if ( index >= 0 ) {
    this.sortFields.splice(index,1)
  }
}

}

